<template lang="pug">
.app-timeline-graph
  svg
</template>

<script>
import * as d3Selection from 'd3-selection';
import { stripZone } from '~/utils/time_utils';
import {
  CHART_HEIGHT,
  CHART_PADDING_TOP,
  RECT_RADIUS_X,
  RECT_RADIUS_Y,
  NEW_STROKE_WIDTH,
  DEFAULT_COLOR,
  LOCATION_CHART_HEIGHT,
  LOCATION_LINE_OFFSET,
  drawTimeline,
} from '~/utils/timeline_graph_utils';

const ResizeObserver = window.ResizeObserver;

// NOTE: This is not included in hub_app to avoid unnecessary imports of d3

export default {
  name: 'AppTimelineGraph',
  props: {
    values: {
      type: Array,
      required: true,
      validator: (values) => {
        // Make sure all objects in the array have the required properties
        const valid = values.filter((v) => !!(v.start && v.stop && v.cssClass));

        return valid.length === values.length;
      },
    },
    date: {
      type: Object,
      required: true,
      validator: (value) => moment.isMoment(value),
    },
    showNow: {
      type: Boolean,
      default: true,
    },
    timeZone: {
      type: String,
      default: 'UTC',
    },
    showLocationLines: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tooltips: [],
      resizeObserver: null,
    };
  },
  computed: {
    dateWithoutZone() {
      return stripZone(this.date);
    },
    timelineStart() {
      return moment(this.dateWithoutZone).startOf('day');
    },
    timelineStop() {
      return moment(this.dateWithoutZone).endOf('day');
    },
    tickValues() {
      return [
        moment(this.dateWithoutZone).set({ hour: 6, minute: 0 }),
        moment(this.dateWithoutZone).set({ hour: 12, minute: 0 }),
        moment(this.dateWithoutZone).set({ hour: 18, minute: 0 }),
      ];
    },
  },
  watch: {
    values: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.setupResizeObserver();
    this.drawChart();
  },
  beforeDestroy() {
    this.stopResizeObserver();
    this.cleanupTooltips();
  },
  methods: {
    setupResizeObserver() {
      // This ResizeObserver serves two purposes:
      // 1. To re-render the chart when the element's dimensions change
      // 2. Detect when we've come out of a display: none situation and need to re-render (e.g., if the timeline is in an initially-hidden dialog)
      // NOTE: ResizeObserver will make an initial callback call when initialized.
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(this.onResize);
      });
      this.resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    onResize() {
      this.drawChart();
    },
    drawValue(rect) {
      rect
        .attr('y', this.showLocationLines ? CHART_PADDING_TOP + LOCATION_LINE_OFFSET : CHART_PADDING_TOP)
        .attr('height', CHART_HEIGHT)
        .attr('rx', RECT_RADIUS_X)
        .attr('ry', RECT_RADIUS_Y)
        .attr('width', this.calculateWidthForValue)
        .attr('x', this.calculateXForValue)
        .attr('fill', (value) => value.color || DEFAULT_COLOR)
        .attr('stroke', (value) => value.color || DEFAULT_COLOR)
        .attr('class', (value) => {
          let cssClass = 'app-timeline-graph--value';

          if (value.cssClass) {
            cssClass += ` ${value.cssClass}`;
          }

          return cssClass;
        })
        .attr('stroke-width', NEW_STROKE_WIDTH)
        .on('click', (event, data) => {
          this.$emit('value-click', { value: data, target: event.target });
        });
    },
    drawLocationLine(line) {
      line
        .attr('class', (location) => `cursor-pointer location-line-${location.meta.locationType}`)
        .attr('width', this.calculateWidthForValue)
        .attr('x', this.calculateXForValue)
        .attr('y', CHART_PADDING_TOP)
        .attr('height', LOCATION_CHART_HEIGHT)
        .attr('rx', 3)
        .attr('ry', 3)
        .on('click', (event, data) => {
          this.$emit('value-click', { value: data, target: event.target });
        });
    },
    calculateWidthForValue(value) {
      if (!value.start || !value.stop) {
        return 1;
      }

      let startTime = stripZone(value.start);
      let stopTime = stripZone(value.stop);

      // Clamp
      if (startTime.isBefore(this.timelineStart)) {
        startTime = this.timelineStart;
      }
      if (stopTime.isAfter(this.timelineStop)) {
        stopTime = this.timelineStop;
      }

      const width = this.xScale(stopTime) - this.xScale(startTime) - NEW_STROKE_WIDTH;

      return Math.max(width, 1);
    },
    calculateXForValue(value) {
      if (!value.start) {
        return 0;
      }

      const startTime = stripZone(value.start);

      return this.xScale(startTime.toDate());
    },
    drawChart() {
      const width = this.$el.getBoundingClientRect().width;

      if (width) {
        const tickFormat = TimeUtils.UserTimeFormats.time;
        const svg = d3Selection.select(this.$el).select('svg');
        const now = this.showNow ? stripZone(moment.tz(this.timeZone)) : null;

        this.cleanupTooltips();

        const { chart, nowTooltip, elTooltip, xScale } = drawTimeline(svg, {
          width,
          now,
          start: this.timelineStart,
          stop: this.timelineStop,
          tickFormat,
          paddingTop: CHART_PADDING_TOP + (this.showLocationLines ? LOCATION_LINE_OFFSET : 0),
        });

        if (nowTooltip) {
          this.tooltips.push(nowTooltip);
        }

        this.xScale = xScale;

        const rect = chart
          .insert('g')
          .attr('class', 'time-entries')
          .selectAll('rect')
          .data(this.values)
          .enter()
          .insert('rect')
          .call(this.drawValue);
        
        if (this.showLocationLines) {
          const locationLines = chart
            .insert('g')
            .attr('class', 'location-lines')
            .selectAll('rect')
            .data(this.values.filter((location) => location.meta.locationType !== 'unknown'))
            .enter()
            .insert('rect')
            .call(this.drawLocationLine);
          
          locationLines
            .exit()
            .each(() => {
              const thisTooltip = elTooltip.get(this);

              if (thisTooltip) {
                thisTooltip.dispose();
              }
            })
            .remove()
        }
        

        svg.selectAll('rect').attr('tabindex', '-1');

        rect
          .exit()
          .each(() => {
            const thisTooltip = elTooltip.get(this);

            if (thisTooltip) {
              thisTooltip.dispose();
            }
          })
          .remove();
      }
    },
    cleanupTooltips() {
      this.tooltips.forEach((tooltip) => tooltip.dispose());
      this.tooltips.splice(0);
    },
  },
};
</script>
